import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

// Bump this when the DPDP Notice PDF is revised (e.g. after legal review).
// Every registration records which version the person actually agreed to.
const DPDP_VERSION = '0.3';

const inputStyle = {
  width:'100%',
  background:'var(--surface2)',
  border:'1px solid var(--border2)',
  color:'var(--text)',
  padding:'11px 14px',
  borderRadius:'8px',
  fontSize:'14px',
  outline:'none',
};

const labelStyle = {
  display:'block',
  fontSize:'11px',
  fontFamily:'DM Mono,monospace',
  color:'var(--text3)',
  letterSpacing:'1px',
  textTransform:'uppercase',
  marginBottom:'6px',
};

const CustomerRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToDpdp, setAgreedToDpdp] = useState(false);
  const [form, setForm] = useState({
    name:'',
    email:'',
    phone:'',
    password:'',
  });

  const update = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.email || !form.phone || !form.password) {
      setError('Please fill all required fields');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!agreedToDpdp) {
      setError('Please read and agree to the Data Protection & Privacy Notice to continue');
      return;
    }

    setLoading(true);
    try {
      await authAPI.customerRegister({ ...form, dpdp_version: DPDP_VERSION });
      navigate('/verify-otp', {
        state: { email: form.email, type: 'customer' }
      });
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight:'100vh',
      background:'var(--bg)',
      display:'flex',
      alignItems:'center',
      justifyContent:'center',
      padding:'20px',
    }}>
      <div style={{ width:'100%', maxWidth:'420px' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <img
            src="/logo512.png"
            alt="IRONWALL Service Bridge"
            style={{
              width:'72px', height:'72px',
              borderRadius:'16px',
              margin:'0 auto 16px',
              display:'block',
            }}
          />
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'26px', fontWeight:'800',
            color:'var(--text)',
            marginBottom:'6px',
          }}>
            IRONWALL
          </h1>
          <p style={{
            fontSize:'13px',
            color:'var(--text3)',
            fontFamily:'DM Mono,monospace',
            letterSpacing:'1px',
          }}>
            CUSTOMER REGISTRATION
          </p>
        </div>

        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'16px',
          padding:'32px',
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom:'16px' }}>
              <label style={labelStyle}>Full Name *</label>
              <input style={inputStyle}
                value={form.name}
                onChange={e => update('name', e.target.value)}
              />
            </div>

            <div style={{ marginBottom:'16px' }}>
              <label style={labelStyle}>Email Address *</label>
              <input type="email" style={inputStyle}
                value={form.email}
                onChange={e => update('email', e.target.value)}
              />
            </div>

            <div style={{ marginBottom:'16px' }}>
              <label style={labelStyle}>Phone *</label>
              <input style={inputStyle}
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
              />
            </div>

            <div style={{ marginBottom:'24px' }}>
              <label style={labelStyle}>Password *</label>
              <input type="password" style={inputStyle}
                placeholder="At least 8 characters"
                value={form.password}
                onChange={e => update('password', e.target.value)}
              />
            </div>

            <div style={{
              display:'flex',
              alignItems:'flex-start',
              gap:'10px',
              marginBottom:'20px',
              padding:'12px 14px',
              background:'var(--surface2)',
              borderRadius:'8px',
              border:'1px solid var(--border2)',
            }}>
              <input
                type="checkbox"
                id="dpdp-agree"
                checked={agreedToDpdp}
                onChange={e => setAgreedToDpdp(e.target.checked)}
                style={{ marginTop:'3px', cursor:'pointer', flexShrink:0 }}
              />
              <label htmlFor="dpdp-agree" style={{
                fontSize:'13px',
                color:'var(--text2)',
                cursor:'pointer',
                lineHeight:1.4,
              }}>
                I have read and agree to the{' '}
                <a
                  href="/DPDP_Notice.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ color:'var(--accent)', textDecoration:'underline' }}
                >
                  Data Protection &amp; Privacy Notice
                </a>
              </label>
            </div>

            {error && (
              <div style={{
                background:'rgba(255,77,109,0.1)',
                border:'1px solid rgba(255,77,109,0.3)',
                color:'var(--red)',
                padding:'12px 14px',
                borderRadius:'8px',
                fontSize:'13px',
                marginBottom:'16px',
              }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading}
              style={{
                width:'100%',
                background: loading ? 'var(--surface3)' : 'var(--accent)',
                color: loading ? 'var(--text3)' : 'var(--bg)',
                border:'none',
                padding:'12px',
                borderRadius:'8px',
                fontSize:'14px',
                fontWeight:'700',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Creating account...' : 'Create Account →'}
            </button>
          </form>

          <p style={{
            textAlign:'center',
            marginTop:'20px',
            fontSize:'13px',
            color:'var(--text3)',
          }}>
            Already registered?{' '}
            <span onClick={() => navigate('/login')}
              style={{ color:'var(--accent)', cursor:'pointer' }}>
              Sign in
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegister;
